import { create } from 'zustand';
import { authAPI, permissionsAPI } from '../api/client';
import { clearChatCache } from '../services/chatCache';
import useChatStore from './useChatStore';
import useShiftsStore from './useShiftsStore';
import {
  decodeJwtPayload,
  isJwtExpired,
  resetSessionExpiredGuard,
  SESSION_EXPIRED_MESSAGE,
  SESSION_EXPIRED_STORAGE_KEY,
} from '../utils/sessionAuth';
import {
  getStoredToken,
  setStoredToken,
  removeStoredToken,
  safeRemoveItem,
  safeSetItem,
} from '../utils/safeStorage';
import { reportBootEvent } from '../utils/bootDiagnostics';
import { shouldUseFastBoot } from '../utils/inAppBrowser';

const VERIFY_MAX_MS = 12_000;
const VERIFY_RETRY_DELAYS = [600, 1500];

let verifyInFlight = null;

function buildUserFromJwt(token) {
  const decoded = decodeJwtPayload(token);
  if (!decoded) return null;
  return {
    userId: decoded.userId,
    username: decoded.username,
    fullName: decoded.fullName,
    role: decoded.role,
    avatar: null,
    isAuthorized: decoded.isAuthorized ?? true,
    requiresShiftOpening: true,
  };
}

function withDeadline(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Bootstrap timeout')), ms);
    }),
  ]);
}

// Загрузка эффективных прав текущего пользователя.
// Для admin/user сервер возвращает permissions: null — это значит «всё разрешено».
async function loadPermissions() {
  try {
    const { data } = await permissionsAPI.getMine();
    return data; // { role, permissions, expires_at, expired }
  } catch (error) {
    console.warn('⚠️ Failed to load /permissions/me:', error.message);
    return null;
  }
}

// Декодируем payload JWT без проверки подписи (только клиентская сторона).
// Используется как fallback когда сервер недоступен — токен уже содержит данные пользователя.
// Реализация — в utils/sessionAuth.js (decodeJwtPayload).

// Загружаем avatar с сервера отдельно (не из токена).
// Avatar может быть большим base64 изображением, поэтому не включаем в JWT.
async function loadUserAvatar(currentToken) {
  try {
    const response = await authAPI.getMe();
    return response.data.avatar ?? null;
  } catch (error) {
    console.warn('⚠️ Failed to load avatar from /auth/me:', error.message);
    return null;
  }
}

const useAuthStore = create((set, get) => {
  const token = getStoredToken();
  const useFastBoot = shouldUseFastBoot() && token && !isJwtExpired(token);
  const fastUser = useFastBoot ? buildUserFromJwt(token) : null;

  return {
    user: fastUser,
    token: token,
    isAuthenticated: Boolean(fastUser),
    loading: Boolean(token && !fastUser),
    error: null,
    permissions: null, // { role, permissions, expires_at, expired } для guest, null для admin/user

    login: async (username, password, secondFactor = {}) => {
    set({ loading: true, error: null });
    try {
      const response = await authAPI.login(username, password, secondFactor);
      if (response.data?.twoFactorRequired) {
        set({ loading: false, error: null });
        return {
          success: false,
          twoFactorRequired: true
        };
      }

      const { token, user } = response.data;

      if (!token || !user) {
        throw new Error('Неверный ответ от сервера');
      }

      setStoredToken(token);
      resetSessionExpiredGuard();
      set({
        user,
        token,
        isAuthenticated: true,
        loading: false,
        error: null
      });

      // Подгружаем эффективные права (для guest — реальные ограничения, для admin/user — null)
      const perms = await loadPermissions();
      set({ permissions: perms });

      console.log('✅ Login successful:', { user, hasToken: !!token });
      return { success: true, twoFactorRequired: false };
    } catch (error) {
      console.error('❌ Login error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Ошибка авторизации';
      set({
        error: errorMessage,
        loading: false
      });
      return {
        success: false,
        twoFactorRequired: false
      };
    }
  },

    verify: async ({ background = false } = {}) => {
      if (verifyInFlight) return verifyInFlight;

      verifyInFlight = (async () => {
        const currentToken = getStoredToken();
        if (!currentToken) {
          set({ isAuthenticated: false, user: null, token: null, loading: false });
          return false;
        }

        if (isJwtExpired(currentToken)) {
          get().handleSessionExpired();
          return false;
        }

        if (background) {
          if (!get().isAuthenticated) get().fastBootFromToken();
        } else {
          set({ loading: true });
        }

        const startedAt = Date.now();
        const MAX_RETRIES = 2;

        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
          const elapsed = Date.now() - startedAt;
          const remaining = VERIFY_MAX_MS - elapsed;
          if (remaining <= 0) break;

          try {
            const response = await withDeadline(authAPI.verify(), remaining);
            const [avatar, perms] = await Promise.all([
              loadUserAvatar(currentToken),
              loadPermissions(),
            ]);

            set({
              user: { ...response.data.user, avatar },
              isAuthenticated: true,
              token: currentToken,
              loading: false,
              permissions: perms,
            });
            return true;
          } catch (error) {
            const isAuthError = error.response?.status === 401
              || error.response?.status === 403
              || error.response?.status === 404;

            if (isAuthError) {
              get().handleSessionExpired(SESSION_EXPIRED_MESSAGE);
              return false;
            }

            if (attempt < MAX_RETRIES && Date.now() - startedAt < VERIFY_MAX_MS) {
              const delay = VERIFY_RETRY_DELAYS[attempt - 1] || 1000;
              // eslint-disable-next-line no-await-in-loop
              await new Promise((resolve) => setTimeout(resolve, delay));
              continue;
            }
          }
        }

        if (background) return get().isAuthenticated;
        return get().forceBootstrapExit();
      })().finally(() => {
        verifyInFlight = null;
      });

      return verifyInFlight;
    },

    /** Мгновенный вход по JWT — для WebKit, где сеть/таймеры «засыпают». */
    fastBootFromToken: () => {
      const currentToken = getStoredToken();
      if (!currentToken || isJwtExpired(currentToken)) return false;
      const cachedUser = buildUserFromJwt(currentToken);
      if (!cachedUser) return false;

      reportBootEvent('webkit_fast_boot');
      set({
        isAuthenticated: true,
        user: cachedUser,
        token: currentToken,
        loading: false,
      });
      return true;
    },

    /** Выход из «зависшей» загрузки: JWT fallback или экран входа. */
    forceBootstrapExit: () => {
      reportBootEvent('force_bootstrap_exit');
      const currentToken = getStoredToken();
      if (currentToken && !isJwtExpired(currentToken)) {
        const cachedUser = buildUserFromJwt(currentToken);
        if (cachedUser) {
          set({ isAuthenticated: true, user: cachedUser, token: currentToken, loading: false });
          return true;
        }
      }

      removeStoredToken();
      set({
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
        permissions: null,
      });
      return false;
    },

    logout: () => {
      const uid = get().user?.userId ?? get().user?.id;
      if (uid) clearChatCache(uid);
      useChatStore.getState().reset();
      removeStoredToken();
      safeRemoveItem(sessionStorage, SESSION_EXPIRED_STORAGE_KEY);
      resetSessionExpiredGuard();
      // Сбрасываем смены чтобы при входе другого пользователя не показывались
      // смены предыдущего (данные в Zustand персистятся между логинами)
      useShiftsStore.getState().reset();
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        permissions: null
      });
    },

    handleSessionExpired: (message = SESSION_EXPIRED_MESSAGE) => {
      const uid = get().user?.userId ?? get().user?.id;
      if (uid) clearChatCache(uid);
      useChatStore.getState().reset();
      useShiftsStore.getState().reset();
      removeStoredToken();
      safeSetItem(sessionStorage, SESSION_EXPIRED_STORAGE_KEY, message);
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: null,
        permissions: null,
      });
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    },

    // Можно ли текущему пользователю выполнить action в section.
    // admin/user — всегда true; guest — проверяем sections[section][action].
    // Значение action может быть true | false | { visibility }; truthy = разрешено.
    can: (section, action = 'view') => {
      const state = get();
      const perms = state.permissions;
      if (!perms || perms.permissions === null) return true; // admin/user
      if (perms.expired) return false;
      const sectionPerm = perms.permissions?.sections?.[section];
      if (!sectionPerm) return false;
      const value = sectionPerm[action];
      return Boolean(value);
    },

    // Возвращает область видимости для action: 'all' | 'own' | 'assigned'.
    // Для admin/user или action со значением true (без объекта) — 'all'.
    getVisibility: (section, action = 'view') => {
      const state = get();
      const perms = state.permissions;
      if (!perms || perms.permissions === null) return 'all';
      const value = perms.permissions?.sections?.[section]?.[action];
      if (typeof value === 'object' && value?.visibility) return value.visibility;
      return 'all';
    },

    // Удобный хелпер: можно ли редактировать/удалять конкретную карточку с учётом видимости.
    // record — объект с полем user_id (создатель) и address_id (адрес).
    canModify: (section, action, record) => {
      const state = get();
      if (!state.can(section, action)) return false;
      const perms = state.permissions;
      if (!perms || perms.permissions === null) return true;
      const visibility = state.getVisibility(section, action);
      if (visibility === 'all') return true;
      if (visibility === 'own') return record?.user_id === state.user?.userId;
      // assigned проверяется на бэке (нужен список адресов гостя) — на фронте считаем разрешённым,
      // если карточка вообще видна (она прошла фильтр на бэке).
      return true;
    },

    isGuest: () => get().permissions?.role === 'guest',

    canUseSectionConstructor: () => Boolean(get().permissions?.features?.sectionConstructor),

    // Нужна ли открытая смена для доступа к рабочим разделам (ShiftGuard, меню и т.д.).
    // admin — обход без смены; guest при requiresShiftOpening=false — без смены; остальные — нужна смена.
    // Открыть смену «по желанию» на главной для админа — отдельно (см. Dashboard showShiftDashboardSection).
    isShiftRequired: () => {
      const user = get().user;
      if (!user) return true;
      if (user.role === 'admin') return false;
      if (user.role === 'guest' && user.requiresShiftOpening === false) return false;
      return true;
    },

    // Возвращает массив разрешённых значений для поля формы или null (если ограничения нет).
    // Для admin/user всегда null (фильтрация не применяется).
    getAllowedValues: (section, formKey, fieldKey) => {
      const state = get();
      const perms = state.permissions;
      if (!perms || perms.permissions === null) return null;
      const allowed = perms.permissions
        ?.sections?.[section]
        ?.forms?.[formKey]
        ?.allowed_values?.[fieldKey];
      return allowed ?? null;
    },

    // Фильтрует переданный массив опций (формат [{value, label}, ...]) по правам гостя.
    // Если ограничения нет — возвращает исходный массив.
    filterOptions: (section, formKey, fieldKey, options) => {
      const allowed = get().getAllowedValues(section, formKey, fieldKey);
      if (allowed === null) return options;
      return options.filter(opt => allowed.includes(opt.value));
    },

    clearError: () => set({ error: null }),

    // Метод для прямого обновления данных пользователя (имя, логин, аватарка)
    updateUser: (updates) => set((state) => {
      if (!state.user) return state;
      return {
        user: { ...state.user, ...updates }
      };
    })
  };
});

export default useAuthStore;
